import {
  ATTRIBUTION_MODEL_RESOLUTION,
  AttributionCauseEventType,
  AttributionEffectEventType,
  AttributionModel,
  AttributionTouchpoint,
  type AttributionSettings,
  type Duration,
} from "@redotech/redo-attribution-common/attribution-settings";
import { restrictToTouchpoints } from "@redotech/redo-attribution-common/touchpoints";
import { MarketingChannel } from "@redotech/redo-billing-common/marketing-channel";
import { durationToSeconds, TimeUnit } from "@redotech/redo-common-model/time";
import {
  marketingAttributionEvent,
  type MarketingAttributionEvent,
} from "@redotech/redo-marketing-templates-common/marketing";
import { objectMapValues } from "@redotech/util/object";

/** What each checkbox on the page means. An SMS send counts as delivered,
 *  since carriers don't report SMS delivery. */
const CAUSES_BY_EVENT: Partial<
  Record<MarketingAttributionEvent, AttributionCauseEventType[]>
> = {
  [marketingAttributionEvent.CLICK]: [
    AttributionCauseEventType.EMAIL_CLICK,
    AttributionCauseEventType.SMS_CLICK,
  ],
  [marketingAttributionEvent.OPEN]: [AttributionCauseEventType.EMAIL_OPEN],
  [marketingAttributionEvent.DELIVERY]: [
    AttributionCauseEventType.EMAIL_DELIVERED,
    AttributionCauseEventType.SMS_SEND,
  ],
};

const FILTERABLE_CAUSES = new Set(Object.values(CAUSES_BY_EVENT).flat());

/** Recover has its own page, so it is left out here. */
const MARKETING_TOUCHPOINTS = [
  AttributionTouchpoint.EMAIL,
  AttributionTouchpoint.SMS,
];

/** The attribution window the marketing analytics surfaces fall back to. */
export const DEFAULT_ATTRIBUTION_DAYS = 5;

/** The day counts the dropdown offers. */
export const SELECTABLE_ATTRIBUTION_WINDOWS = [
  1, 2, 3, 4, 5, 7, 10, 14, 21, 30,
] as const;

const TOUCHPOINT_BY_CHANNEL: Record<MarketingChannel, AttributionTouchpoint> = {
  [MarketingChannel.EMAIL]: AttributionTouchpoint.EMAIL,
  [MarketingChannel.SMS]: AttributionTouchpoint.SMS,
};

/**
 * The team's saved settings with the analytics page's filters applied. Causes
 * the page cannot name keep whatever the settings say.
 */
export function analyticsAttributionSettings({
  settings,
  attributionDays,
  attributionEvents,
  channels,
}: {
  settings: AttributionSettings;
  attributionDays: number;
  attributionEvents: readonly MarketingAttributionEvent[];
  channels?: readonly MarketingChannel[];
}): AttributionSettings {
  const selected = new Set(
    attributionEvents.flatMap((event) => CAUSES_BY_EVENT[event] ?? []),
  );
  const window: Duration = { value: attributionDays, unit: TimeUnit.DAY };
  const order = settings[AttributionEffectEventType.ORDER];
  const inferred = { ...order.inferred };
  for (const cause of FILTERABLE_CAUSES) {
    const config = inferred[cause];
    if (config) {
      inferred[cause] = { ...config, enabled: selected.has(cause), window };
    }
  }
  // The direct model credits a touchpoint rather than a cause event, so the
  // page's event filter cannot reach it, but its window still can.
  const direct = objectMapValues(order.direct, (config) =>
    config ? { ...config, window } : config,
  );
  const withFilters: AttributionSettings = {
    [AttributionEffectEventType.ORDER]: { ...order, inferred, direct },
  };

  const keep = channels?.length
    ? channels.map((channel) => TOUCHPOINT_BY_CHANNEL[channel])
    : Object.values(TOUCHPOINT_BY_CHANNEL);
  return restrictToTouchpoints(withFilters, keep);
}

const windowInDays = ({ value, unit }: Duration) =>
  durationToSeconds(value, unit) / durationToSeconds(1, TimeUnit.DAY);

/** A saved window the dropdown can't show would leave it blank, so snap to
 *  the closest one it can. */
function nearestSelectableWindow(days: number): number {
  return SELECTABLE_ATTRIBUTION_WINDOWS.reduce((best, value) =>
    Math.abs(value - days) < Math.abs(best - days) ? value : best,
  );
}

/** Cause-event filters only reach the models that infer credit from them. */
export function attributionEventsApply(settings: AttributionSettings): boolean {
  const model = settings[AttributionEffectEventType.ORDER].defaultModel;
  return ATTRIBUTION_MODEL_RESOLUTION[model].qualifiers[
    AttributionModel.INFERRED
  ];
}

/** Every checkbox the page offers. */
export const FILTERABLE_ATTRIBUTION_EVENTS = Object.keys(
  CAUSES_BY_EVENT,
) as MarketingAttributionEvent[];

/** What the page opens with, taken from the team's attribution settings. */
export interface AnalyticsAttributionFilters {
  attributionDays: number;
  attributionEvents: MarketingAttributionEvent[];
}

export function analyticsFiltersFromSettings(
  settings: AttributionSettings,
): AnalyticsAttributionFilters {
  const order = settings[AttributionEffectEventType.ORDER];
  const inferred = order.inferred;
  const qualifiers =
    ATTRIBUTION_MODEL_RESOLUTION[order.defaultModel].qualifiers;
  // The Discount model turns every cause off. That means "these checkboxes
  // don't apply here", not "the user unchecked them all".
  const attributionEvents = qualifiers[AttributionModel.INFERRED]
    ? Object.values(marketingAttributionEvent).filter((event) =>
        (CAUSES_BY_EVENT[event] ?? []).some(
          (cause) => inferred[cause]?.enabled,
        ),
      )
    : FILTERABLE_ATTRIBUTION_EVENTS;

  // Settings store a window per cause and per touchpoint, but the page has one
  // dropdown, so show the longest of the ones this model actually uses.
  const configured = [
    ...(qualifiers[AttributionModel.INFERRED]
      ? [...FILTERABLE_CAUSES].map((cause) => inferred[cause])
      : []),
    ...(qualifiers[AttributionModel.DIRECT]
      ? MARKETING_TOUCHPOINTS.map((touchpoint) => order.direct[touchpoint])
      : []),
  ];
  const windows = configured.flatMap((config) =>
    config?.enabled ? [windowInDays(config.window)] : [],
  );
  // A window can be saved in hours, shorter than any option.
  const longest = windows.length
    ? Math.max(...windows)
    : DEFAULT_ATTRIBUTION_DAYS;
  return {
    attributionDays: nearestSelectableWindow(longest),
    attributionEvents,
  };
}
